import React from "react";
import { VLYT } from "@/constants/testIds/vlyt";
import useReveal from "@/hooks/useReveal";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function TheHandshake() {
  const { ref, inView } = useReveal({ threshold: 0.25 });

  return (
    <section
      ref={ref}
      className="relative py-28 md:py-40 border-b border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)]"
      data-testid={VLYT.handshake.section}
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className={`reveal ${inView ? "in" : ""} max-w-3xl`}>
          <div className="text-eyebrow mb-5">02 · The Handshake</div>
          <h2 className="text-h1 font-display">
            A <span className="text-lime">zero-leakage</span> handshake between rider and driver.
          </h2>
          <p className="mt-6 text-[color:var(--vlyt-muted)] text-[16px]">
            No middlemen. No leaked fees. A cryptographic 3-node exchange between rider, Vlyt trust
            layer, and driver, verified in under a second.
          </p>
        </div>

        {/* Diagram */}
        <div className={`reveal ${inView ? "in" : ""} mt-16`} style={{ transitionDelay: "140ms" }}>
          <div className="relative rounded-[24px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-base)] p-6 md:p-12 overflow-hidden">
            <svg
              viewBox="0 0 1000 340"
              className="w-full h-auto"
              role="img"
              aria-label="Zero-leakage architecture diagram"
            >
              <defs>
                <linearGradient id="line-lime" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="var(--vlyt-purple)" />
                  <stop offset="50%" stopColor="var(--vlyt-lime)" />
                  <stop offset="100%" stopColor="var(--vlyt-purple)" />
                </linearGradient>
                <radialGradient id="node-glow" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="rgba(191,255,0,0.35)" />
                  <stop offset="100%" stopColor="rgba(191,255,0,0)" />
                </radialGradient>
              </defs>

              {/* connecting lines */}
              {inView && (
                <>
                  <path
                    id="path-rd"
                    d="M 170 170 C 340 60, 500 60, 500 170"
                    fill="none"
                    stroke="url(#line-lime)"
                    strokeWidth="2"
                    className="line-draw"
                    style={{ animationDelay: "200ms" }}
                  />
                  <path
                    id="path-dr"
                    d="M 500 170 C 500 280, 660 280, 830 170"
                    fill="none"
                    stroke="url(#line-lime)"
                    strokeWidth="2"
                    className="line-draw"
                    style={{ animationDelay: "600ms" }}
                  />
                  {/* traveling packets */}
                  <circle r="5" fill="#BFFF00" style={{ offsetPath: "path('M 170 170 C 340 60, 500 60, 500 170')", offsetRotate: "0deg" }} className="packet-travel" />
                  <circle r="5" fill="#A87BFF" style={{ offsetPath: "path('M 500 170 C 500 280, 660 280, 830 170')", offsetRotate: "0deg", animationDelay: "1s" }} className="packet-travel" />
                </>
              )}

              {/* halo behind center */}
              <circle cx="500" cy="170" r="80" fill="url(#node-glow)" />

              {/* rider node */}
              <g data-testid={VLYT.handshake.nodeRider}>
                <circle cx="170" cy="170" r="46" fill="var(--vlyt-surface-1)" stroke="var(--vlyt-border)" strokeWidth="1.5" />
                <circle cx="170" cy="170" r="46" fill="none" stroke="var(--vlyt-lime)" strokeOpacity="0.35" strokeWidth="1" />
                <text x="170" y="167" textAnchor="middle" fontFamily="Space Grotesk" fontWeight="700" fontSize="16" fill="var(--vlyt-text)">Rider</text>
                <text x="170" y="184" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="var(--vlyt-muted)" letterSpacing="2">STUDENT</text>
              </g>
              <text x="170" y="248" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="var(--vlyt-muted)" letterSpacing="3">01 · REQUEST</text>

              {/* vlyt center node */}
              <g data-testid={VLYT.handshake.nodeVlyt}>
                <circle cx="500" cy="170" r="60" fill="var(--vlyt-base)" stroke="var(--vlyt-lime)" strokeWidth="1.5" />
                <circle cx="500" cy="170" r="60" fill="none" stroke="var(--vlyt-lime)" strokeOpacity="0.25" strokeWidth="8" className="auto-pulse-dot" />
                <text x="500" y="167" textAnchor="middle" fontFamily="Space Grotesk" fontWeight="700" fontSize="18" fill="var(--vlyt-lime)">VLYT</text>
                <text x="500" y="184" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="var(--vlyt-purple-soft)" letterSpacing="2">TRUST LAYER</text>
              </g>
              <text x="500" y="258" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="var(--vlyt-muted)" letterSpacing="3">02 · MATCH · VERIFY</text>

              {/* driver node */}
              <g data-testid={VLYT.handshake.nodeDriver}>
                <circle cx="830" cy="170" r="46" fill="var(--vlyt-surface-1)" stroke="var(--vlyt-border)" strokeWidth="1.5" />
                <circle cx="830" cy="170" r="46" fill="none" stroke="var(--vlyt-purple-soft)" strokeOpacity="0.4" strokeWidth="1" />
                <text x="830" y="167" textAnchor="middle" fontFamily="Space Grotesk" fontWeight="700" fontSize="16" fill="var(--vlyt-text)">Driver</text>
                <text x="830" y="184" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="var(--vlyt-muted)" letterSpacing="2">PARTNER</text>
              </g>
              <text x="830" y="248" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="var(--vlyt-muted)" letterSpacing="3">03 · CONFIRM</text>
            </svg>

            {/* No-SIM callout */}
            <div
              data-testid={VLYT.handshake.noSim}
              className="mt-10 grid md:grid-cols-2 gap-6"
            >
              <div className="rounded-[16px] border border-[color:var(--vlyt-border)] p-6 flex items-start gap-4 card-hover">
                <div className="h-10 w-10 rounded-lg bg-[rgba(191,255,0,0.08)] flex items-center justify-center ring-1 ring-[color:var(--vlyt-lime)]">
                  <KeyRound size={18} className="text-lime" />
                </div>
                <div>
                  <div className="font-display text-[18px]">No-SIM Authentication</div>
                  <p className="text-[color:var(--vlyt-muted)] text-[14px] mt-1">
                    Our proprietary &lsquo;Zero-Leakage&rsquo; architecture utilizes a Physical ID
                    Escrow Handshake. Funds are locked in a digital vault at booking and only
                    released to the driver upon a verified, in-person matric card verification.
                  </p>
                </div>
              </div>
              <div className="rounded-[16px] border border-[color:var(--vlyt-border)] p-6 flex items-start gap-4 card-hover">
                <div className="h-10 w-10 rounded-lg bg-[rgba(168,123,255,0.10)] flex items-center justify-center ring-1 ring-[color:var(--vlyt-purple-soft)]">
                  <ShieldCheck size={18} className="text-[color:var(--vlyt-purple-soft)]" />
                </div>
                <div>
                  <div className="font-display text-[18px]">Zero-Leakage Fees</div>
                  <p className="text-[color:var(--vlyt-muted)] text-[14px] mt-1">
                    Vlyt maintains a high-throughput economy through a flat ₦10 Platform Access
                    Fee. Drivers retain 95 percent of every standard fare, ensuring absolute
                    supply-side retention and a sustainable operational foundation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
